import { Link } from "react-router-dom";
import type { PricingQuoteResponse, ProductCard } from "@itaas/pep-contracts";
import { ProductEditionShowcase } from "../../components/product/ProductEditionShowcase";
import { ProductContentSection } from "../../components/product/ProductContentSection";
import { ProductPromotionBadge } from "../../components/commercial/ProductPromotionBadge";
import { ProductVerticalCrossLinks } from "../../components/product/ProductVerticalExtras";
import { PublisherTrustStrip } from "../../components/trust/InstitutionalTrust";
import { ProductCardView } from "../../components/catalog/ProductCard";
import type { PepLocale } from "../../i18n/types";
import { labelCategory, productTagline } from "../../utils/labels";
import {
  buildConfigureHrefForProduct,
  commercialCtaLabelForProduct,
} from "../../utils/commercial-cta";
import { isPromotionActive } from "../../utils/product-promotion";
import { SUITE_URL } from "../../config";
import {
  buildProductTrustChips,
  pickLocale,
  type ProductDetailHdConfig,
} from "./product-detail-types";
import { ProductAudienceGrid } from "./ProductAudienceGrid";
import { ProductCapabilitiesGrid } from "./ProductCapabilitiesGrid";
import { ProductCompliancePanel } from "./ProductCompliancePanel";
import { ProductDetailHeroHd } from "./ProductDetailHeroHd";
import { ProductFinalCtaBand } from "./ProductFinalCtaBand";
import { ProductOutcomesGrid } from "./ProductOutcomesGrid";
import { ProductPricingAside } from "./ProductPricingAside";
import { ProductVisualShowcase } from "./ProductVisualShowcase";

export interface ProductDetailHdPageProps {
  locale: PepLocale;
  product: ProductCard & { complementary?: ProductCard[] };
  prices: Record<string, PricingQuoteResponse>;
  description: string;
  showDemo: boolean;
  showTrial: boolean;
  config: ProductDetailHdConfig;
}

/**
 * Shared HD product detail layout — data-driven via ProductDetailHdConfig.
 * Used by Edu, RH, and future Suite products (CRM, Invoice, …).
 */
export function ProductDetailHdPage({
  locale,
  product,
  prices,
  description,
  showDemo,
  showTrial,
  config,
}: ProductDetailHdPageProps) {
  const name =
    locale === "en"
      ? (product.displayNameEn ?? product.displayName)
      : (product.displayNameFr ?? product.displayName);
  const tagline = productTagline(product, locale);
  const catalogKicker =
    locale === "en" && product.heroKickerEn
      ? product.heroKickerEn
      : product.heroKickerFr;
  const kicker =
    catalogKicker ??
    (config.heroKicker ? pickLocale(config.heroKicker, locale) : undefined);
  const configureTo = buildConfigureHrefForProduct(product);
  const demoTo = `/contact?product=${product.slug}&type=demo`;
  const contactTo = `/contact?product=${product.slug}&type=info`;
  const demoFirst = config.ctaPriority === "demo" && showDemo;
  const contactFirst =
    config.ctaPriority === "contact" || (!showTrial && !showDemo && config.ctaPriority !== "configure");

  const primaryCta = demoFirst
    ? {
        to: demoTo,
        label: locale === "en" ? "See a demonstration" : "Voir la démonstration",
      }
    : contactFirst
      ? {
          to: contactTo,
          label: locale === "en" ? "Contact us" : "Nous contacter",
        }
      : {
          to: configureTo,
          label: commercialCtaLabelForProduct(product, locale),
        };

  const secondaryCta = demoFirst
    ? {
        to: contactTo,
        label: locale === "en" ? "Contact us" : "Nous contacter",
      }
    : contactFirst
      ? undefined
      : showDemo
        ? {
            to: demoTo,
            label: locale === "en" ? "See a demonstration" : "Voir la démonstration",
          }
        : undefined;

  const chips = buildProductTrustChips(config.trustBaseChips, {
    trialDays:
      product.portfolio?.trialAllowed === false ? undefined : product.trialDays,
    promoActive: isPromotionActive(product.promotion),
    promoBadgeFr: product.promotion?.badgeFr,
    promoBadgeEn: product.promotion?.badgeEn,
  }).map((c) => ({
    id: c.id,
    label: locale === "en" ? c.labelEn : c.labelFr,
  }));

  const complianceHighlights = (product.highlights ?? []).slice(0, 3);
  const capabilityGroups =
    config.preferConfigCapabilities && config.capabilityGroups?.length
      ? config.capabilityGroups
      : (product.featureGroups?.length ? product.featureGroups : null) ??
        config.capabilityGroups ??
        [];

  const visualCaption =
    config.visualCaption != null
      ? pickLocale(config.visualCaption, locale)
      : undefined;

  return (
    <div className="pep-pd-hd">
      <ProductDetailHeroHd
        locale={locale}
        productSlug={product.slug}
        productName={name}
        categoryLabel={labelCategory(product.category, locale)}
        kicker={kicker}
        tagline={tagline}
        description={description}
        primaryCta={primaryCta}
        secondaryCta={secondaryCta}
        trustChips={chips}
        promoBadge={
          isPromotionActive(product.promotion) ? (
            <ProductPromotionBadge promotion={product.promotion} locale={locale} />
          ) : undefined
        }
        visual={
          <ProductVisualShowcase
            locale={locale}
            productImageSrc={config.productImageSrc}
            productImageAlt={pickLocale(config.productImageAlt, locale)}
            humanImageSrc={config.humanImageSrc}
            humanImageAlt={pickLocale(config.humanImageAlt, locale)}
            caption={visualCaption}
          />
        }
      />

      <ProductAudienceGrid
        locale={locale}
        items={config.audiences}
        title={pickLocale(config.audienceTitle, locale)}
      />

      <ProductCapabilitiesGrid locale={locale} groups={capabilityGroups} />

      <ProductOutcomesGrid
        locale={locale}
        items={config.outcomes}
        lead={pickLocale(config.outcomesLead, locale)}
      />

      <section className="pep-pd-section pep-pd-section--muted">
        <div className="container">
          <div className="pep-pd-split">
            <div className="pep-pd-split__main">
              <ProductCompliancePanel
                locale={locale}
                highlights={complianceHighlights}
                title={pickLocale(config.contextTitle, locale)}
                lead={pickLocale(config.contextLead, locale)}
                fallbackPoints={
                  complianceHighlights.length
                    ? undefined
                    : config.contextFallbackPoints?.map((p) =>
                        pickLocale(p, locale),
                      )
                }
              />
            </div>
            <div className="pep-pd-split__aside">
              <ProductPricingAside
                locale={locale}
                product={product}
                prices={prices}
                configureTo={demoFirst ? demoTo : configureTo}
                primaryCtaLabel={demoFirst ? primaryCta.label : undefined}
                showPricingLink={product.portfolio?.pricingAllowed !== false}
              />
              <div className="pep-pd-aside-card mt-3">
                <h3 className="h6 fw-bold mb-2">
                  {locale === "en" ? "Already a customer?" : "Déjà client ?"}
                </h3>
                <a className="btn btn-outline-secondary btn-sm w-100" href={SUITE_URL}>
                  {locale === "en" ? "Open ITaaS Suite" : "Accéder à la Suite ITaaS"}
                </a>
              </div>
              {config.showAmbassadorLink ? (
                <div className="pep-pd-aside-card mt-3">
                  <Link
                    className="btn btn-outline-success btn-sm w-100"
                    to={`/affiliation/inscription?product=${product.slug}`}
                  >
                    {config.ambassadorLabel
                      ? pickLocale(config.ambassadorLabel, locale)
                      : locale === "en"
                        ? "Become an ambassador"
                        : "Devenir ambassadeur"}
                  </Link>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {config.showEditionShowcase !== false ? (
        <section className="pep-pd-section pep-pd-section--muted">
          <div className="container">
            <h2 className="pep-pd-section__title">
              {locale === "en" ? "Editions" : "Éditions"}
            </h2>
            <ProductEditionShowcase product={product} prices={prices} locale={locale} />
          </div>
        </section>
      ) : null}

      <div className="container">
        <ProductContentSection productSlug={product.slug} />
        <ProductVerticalCrossLinks product={product} locale={locale} />
      </div>

      {product.complementary && product.complementary.length > 0 ? (
        <section className="pep-pd-section">
          <div className="container">
            <h2 className="pep-pd-section__title">
              {locale === "en" ? "Complementary products" : "Produits complémentaires"}
            </h2>
            <div className="row g-4">
              {product.complementary.map((p) => (
                <div className="col-md-4" key={p.slug}>
                  <ProductCardView product={p} />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <ProductFinalCtaBand
        locale={locale}
        productName={name}
        primary={primaryCta}
        secondary={
          secondaryCta ??
          (contactFirst
            ? undefined
            : {
                to: contactTo,
                label: locale === "en" ? "Contact us" : "Nous contacter",
              })
        }
      />

      <div className="container py-4">
        <PublisherTrustStrip compact />
      </div>
    </div>
  );
}
